import React from 'react'
import { Stack, Typography } from '@mui/material'
import AiCard from './cardKit/AiCard'
import { formatInquiryRupee, type TradeFill, type TradeHistory } from './domains/tradingDomain'

type Props = {
    history: TradeHistory
}

/** Fill time in IST, the exchange's own clock. */
function fillTime(value: string | null | undefined): string | null {
    if (!value) return null
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return null
    return parsed.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata',
    })
}

function FillRow({ fill }: { fill: TradeFill }) {
    const price = formatInquiryRupee(fill.price)
    const time = fillTime(fill.tradedAt)
    const isBuy = fill.side === 'BUY'
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} sx={{ mt: 0.75 }}>
            <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} color={isBuy ? 'buy.main' : 'sell.main'}>
                    {fill.side}
                </Typography>
                <Typography variant="body2" sx={{ minWidth: 0 }}>
                    {fill.quantity} {fill.instrumentLabel}
                </Typography>
            </Stack>
            <Typography variant="body2" fontWeight={600} className="font-feature-num">
                {price ?? ''}
                {time ? (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        {time}
                    </Typography>
                ) : null}
            </Typography>
        </Stack>
    )
}

export default function TradeHistoryCard({ history }: Props) {
    if (history.outcome !== 'MATCHED' || history.trades.length === 0) {
        return null
    }
    const hidden = history.totalCount - history.trades.length
    return (
        <AiCard accent="neutral">
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="subtitle2" fontWeight={700}>
                    Today&apos;s trades
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {history.totalCount} fill{history.totalCount === 1 ? '' : 's'}
                </Typography>
            </Stack>
            {history.trades.map((fill, index) => (
                <FillRow key={`${fill.securityCode}-${fill.tradedAt ?? index}`} fill={fill} />
            ))}
            {hidden > 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    +{hidden} more
                </Typography>
            ) : null}
        </AiCard>
    )
}
